import { normalizeIngredientName } from '../ingredients/normalize-name.js';
const RANK = { EXACT: 0, LIKELY: 1, POSSIBLE: 2 };
/** "Tomaten" and "Tomate", "Eier" and "Ei": German endings the normalizer leaves alone. */
function stem(word) {
    if (word.length <= 4)
        return word;
    return word.replace(/(?:en|er|n|e|s)$/, '');
}
const stemmed = (normalized) => normalized.split(' ').map(stem).join(' ');
function sameName(a, b) {
    if (!a || !b)
        return false;
    return a === b || stemmed(a) === stemmed(b);
}
/** A word of the item occurs in the product's words: equal, a prefix, or the head of a compound. */
function wordOccurs(word, tokens) {
    const wordStem = stem(word);
    return tokens.some((token) => {
        if (token === word || stem(token) === wordStem)
            return true;
        if (wordStem.length < 4)
            return false;
        return token.startsWith(wordStem) || token.endsWith(word) || stem(token).endsWith(wordStem);
    });
}
const lookup = (learned, key) => {
    if (!learned)
        return undefined;
    if (learned instanceof Map)
        return learned.get(key);
    return learned[key];
};
export function matchProductToItems(product, items, learned) {
    const productName = normalizeIngredientName(product.name);
    const ingredientNames = (product.ingredientNames ?? []).map(normalizeIngredientName);
    const tokens = [product.brand ?? '', product.name, ...(product.ingredientNames ?? [])]
        .map(normalizeIngredientName)
        .join(' ')
        .split(' ')
        .filter(Boolean);
    const candidates = [];
    items.forEach((item, index) => {
        const name = normalizeIngredientName(item.name);
        let match = null;
        if (item.productId && item.productId === product.id)
            match = { confidence: 'EXACT', reason: 'PRODUCT' };
        else if (name && lookup(learned, name) === product.id)
            match = { confidence: 'EXACT', reason: 'LEARNED' };
        else if (item.ingredientId &&
            product.ingredientId &&
            item.ingredientId === product.ingredientId)
            match = { confidence: 'EXACT', reason: 'INGREDIENT' };
        else if (ingredientNames.some((candidate) => sameName(name, candidate)))
            match = { confidence: 'LIKELY', reason: 'INGREDIENT_NAME' };
        else if (sameName(name, productName))
            match = { confidence: 'LIKELY', reason: 'PRODUCT_NAME' };
        else {
            const words = name.split(' ').filter(Boolean);
            if (words.length && words.every((word) => wordOccurs(word, tokens)))
                match = { confidence: 'POSSIBLE', reason: 'WORDS' };
        }
        if (match)
            candidates.push({
                itemId: item.id,
                ...match,
                checked: item.checked,
                ...(item.checked && item.productId && item.productId !== product.id
                    ? { otherProduct: true }
                    : {}),
                order: item.order ?? index,
            });
    });
    // Open items before bought ones, then by confidence, then the older item first.
    return candidates
        .sort((a, b) => Number(a.checked) - Number(b.checked) ||
        RANK[a.confidence] - RANK[b.confidence] ||
        a.order - b.order)
        .map(({ order: _order, ...candidate }) => candidate);
}
export function decideScanMatch(candidates, mode) {
    const open = candidates.filter((c) => !c.checked);
    if (open.length) {
        const exact = open.filter((c) => c.confidence === 'EXACT');
        // An item that points at this very product wins over look-alikes; two of them do not.
        if (exact.length === 1)
            return { kind: 'PROPOSE', candidate: exact[0], auto: mode === 'AUTO' };
        if (open.length === 1 && open[0].confidence === 'LIKELY')
            return { kind: 'PROPOSE', candidate: open[0], auto: false };
        return { kind: 'CHOOSE', candidates: [...open] };
    }
    const confident = candidates.filter((c) => c.confidence !== 'POSSIBLE');
    const bought = confident.find((c) => !c.otherProduct);
    if (bought)
        return { kind: 'ALREADY_BOUGHT', candidate: bought };
    const other = confident.find((c) => c.otherProduct);
    return other ? { kind: 'OTHER_PRODUCT_BOUGHT', candidate: other } : { kind: 'NONE' };
}
//# sourceMappingURL=match-product.js.map